import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  useToast,
  Card,
  CardHeader,
  CardBody,
  Divider,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Flex,
  Checkbox,
  CheckboxGroup,
  Textarea,
  FormControl
} from '@chakra-ui/react';
import apiClient from '../lib/api/apiClient';
import InviteUserModal from './InviteUserModal';
import TeamOwnership from './TeamOwnership';
import { useTeamRoster } from '../hooks/useTeamRoster';
import { useTeam } from '../hooks/useTeam';
import { useTransactions } from '../hooks/useTransactions';
import { useUserContext } from '../contexts/UserContext';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { useNavigate } from 'react-router-dom';

interface JoinRequest {
  id: number;
  team_id: number;
  league_id: number;
  user_id: number;
  user: {
    display_name: string;
    email: string;
  }
  requested_roles: string[];
  message: string;
  status: 'pending' | 'approved' | 'declined';
  created_at: string;
}

interface FrontOfficeProps {
  teamId: number;
  leagueId: number;
  teamName?: string;
}

export const FrontOffice: React.FC<FrontOfficeProps> = ({ teamId, leagueId, teamName }) => {
  const navigate = useNavigate();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedRequest, setSelectedRequest] = useState<JoinRequest | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [responseMessage, setResponseMessage] = useState<string>('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isInviteOpen, onOpen: onInviteOpen, onClose: onInviteClose } = useDisclosure();
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.400');
  const messageBgColor = useColorModeValue('gray.50', 'gray.700');
  const textareaBgColor = useColorModeValue('white', 'gray.700');

  // Get team and payroll data
  const { team } = useTeam(teamId);
  const { total_payroll } = useTeamRoster(teamId);
  
  // Get league and user context for transactions
  const myLeague = useMyLeagueStore(state => state.myLeague);
  const { myTeam } = useUserContext();
  
  // Check if this is the user's team
  const isMyTeam = myTeam !== null && Number(myTeam) === Number(teamId);
  
  // Get recent transactions (10 most recent)
  const { data: transactionsData, isLoading: transactionsLoading } = useTransactions({
    leagueId: leagueId || myLeague?.id,
    filters: {
      teamIds: [teamId],
      myTeam: myTeam ? Number(myTeam) : undefined,
    },
    page: 0,
    pageSize: 10,
  });
  
  const recentTransactions = transactionsData?.transactions || [];

  // Refresh trigger function to update TeamOwnership data
  const triggerRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleViewRequest = (request: JoinRequest) => {
    setSelectedRequest(request);
    setSelectedRoles(request.requested_roles); // Initialize with all requested roles selected
    setResponseMessage(''); // Reset response message
    onOpen();
  };

  const handleApprove = async (requestId: number, approvedRoles?: string[]) => {
    try {
      const payload: { approved_roles?: string[]; response_message?: string } = {};
      if (approvedRoles) {
        payload.approved_roles = approvedRoles;
      }
      if (responseMessage.trim()) {
        payload.response_message = responseMessage.trim();
      }
      
      await apiClient.patch(`/team/join-requests/${requestId}/approve`, payload);
      
      const roleText = approvedRoles && approvedRoles.length > 0 
        ? ` for roles: ${approvedRoles.join(', ')}`
        : '';
      
      toast({
        title: 'Request Approved',
        description: `The join request has been approved${roleText}.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Refresh the list
      triggerRefresh();
      onClose();
    } catch (err) {
      console.error('Error approving request:', err);
      const errorMessage = err && typeof err === 'object' && 'response' in err && err.response && typeof err.response === 'object' && 'data' in err.response && err.response.data && typeof err.response.data === 'object' && 'message' in err.response.data && typeof err.response.data.message === 'string' 
        ? err.response.data.message 
        : 'Failed to approve request';
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDecline = async (requestId: number) => {
    try {
      const payload: { response_message?: string } = {};
      if (responseMessage.trim()) {
        payload.response_message = responseMessage.trim();
      }
      
      await apiClient.patch(`/team/join-requests/${requestId}/decline`, payload);
      
      toast({
        title: 'Request Declined',
        description: 'The join request has been declined.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });

      // Refresh the list
      triggerRefresh();
      onClose();
    } catch (err) {
      console.error('Error declining request:', err);
      const errorMessage = err && typeof err === 'object' && 'response' in err && err.response && typeof err.response === 'object' && 'data' in err.response && err.response.data && typeof err.response.data === 'object' && 'message' in err.response.data && typeof err.response.data.message === 'string'
        ? err.response.data.message
        : 'Failed to decline request';
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { colorScheme: 'yellow', label: 'Pending' },
      accepted: { colorScheme: 'green', label: 'Approved' },
      declined: { colorScheme: 'red', label: 'Declined' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    
    return (
      <Badge colorScheme={config.colorScheme} fontSize="sm">
        {config.label}
      </Badge>
    );
  };

  return (
    <Box w="100%" p={6}>
      <Flex direction={{ base: "column", md: "row" }} gap={6} align="stretch">
        {/* Left Panel - Action Items and Recent Transactions */}
        <Box flex={{ base: "1", md: "0 0 30%" }} minW={0}>
          <VStack spacing={6} align="stretch">
            {isMyTeam && (
              <Card bg={bgColor} borderColor={borderColor} borderWidth="1px">
                <CardHeader>
                  <Heading size="md" textAlign="left">
                    Action Items
                  </Heading>
                </CardHeader>
                <CardBody>
                  <Text color={secondaryTextColor} fontStyle="italic">
                    Nothing needs doing. <br />
                    You're all caught up!
                  </Text>
                </CardBody>
              </Card>
            )}

            <Card bg={bgColor} borderColor={borderColor} borderWidth="1px">
              <CardHeader>
                <Flex justify="space-between" align="center">
                  <Heading size="md" textAlign="left">
                    Recent Transactions
                  </Heading>
                  <Button
                    size="sm"
                    colorScheme="green"
                    onClick={() => navigate("/transactions")}
                  >
                    ALL »
                  </Button>
                </Flex>
              </CardHeader>
              <CardBody>
                {transactionsLoading ? (
                  <Text color={secondaryTextColor} fontSize="sm">
                    Loading...
                  </Text>
                ) : recentTransactions.length === 0 ? (
                  <Text
                    color={secondaryTextColor}
                    fontSize="sm"
                    fontStyle="italic"
                  >
                    {isMyTeam
                      ? "No recent transactions"
                      : `No recent transactions for ${teamName || "this team"}`}
                  </Text>
                ) : (
                  <VStack spacing={2} align="stretch">
                    {recentTransactions
                      .slice(0, 10)
                      .map(
                        (transaction: {
                          id: number;
                          headline?: string;
                          type_display?: string;
                          type?: string;
                          created_at?: string;
                          createdAt?: string;
                        }) => (
                          <Box
                            key={transaction.id}
                            pb={2}
                            borderBottom="1px solid"
                            borderColor={borderColor}
                            _last={{ borderBottom: "none" }}
                          >
                            <Text
                              fontSize="sm"
                              color={textColor}
                              fontWeight="medium"
                              textAlign="left"
                            >
                              {transaction.headline ||
                                transaction.type_display ||
                                transaction.type ||
                                "Transaction"}
                            </Text>
                            {(transaction.created_at ||
                              transaction.createdAt) && (
                              <Text
                                fontSize="xs"
                                color={secondaryTextColor}
                                textAlign="left"
                              >
                                {(() => {
                                  const dateStr =
                                    transaction.created_at ||
                                    transaction.createdAt;
                                  if (!dateStr) return "";
                                  const date = new Date(dateStr);
                                  return `${date.toLocaleDateString()} ${date.toLocaleTimeString(
                                    [],
                                    { hour: "2-digit", minute: "2-digit" }
                                  )}`;
                                })()}
                              </Text>
                            )}
                          </Box>
                        )
                      )}
                  </VStack>
                )}
              </CardBody>
            </Card>
          </VStack>
        </Box>

        {/* Middle Panel - Team Ownership */}
        <Box flex={{ base: "1", md: "0 0 40%" }} minW={0}>
          <TeamOwnership
            teamId={teamId}
            leagueId={leagueId}
            onInviteOpen={isMyTeam ? onInviteOpen : undefined}
            onViewRequest={isMyTeam ? handleViewRequest : undefined}
            refreshTrigger={refreshTrigger}
            isMyTeam={isMyTeam}
          />
        </Box>

        {/* Right Panel - Financials */}
        <Box flex={{ base: "1", md: "0 0 30%" }} minW={0}>
          <Card bg={bgColor} borderColor={borderColor} borderWidth="1px">
            <CardHeader>
              <Heading size="md" textAlign="left">
                Financials
              </Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={2} align="stretch" alignItems="flex-start">
                <Text fontSize="sm" color={secondaryTextColor} textAlign="left">
                  Salary Cap
                </Text>
                {team?.salary_cap !== undefined && (
                  <Text
                    fontSize="md"
                    fontWeight="medium"
                    color={textColor}
                    textAlign="left"
                  >
                    ${Number(team.salary_cap).toLocaleString()}M
                    {team.salaryCapDescription && (
                      <Text as="span" color={secondaryTextColor} ml={2}>
                        ({team.salaryCapDescription})
                      </Text>
                    )}
                  </Text>
                )}
                <Text
                  fontSize="sm"
                  color={secondaryTextColor}
                  mt={2}
                  textAlign="left"
                >
                  Current Payroll
                </Text>
                <Text
                  fontSize="md"
                  fontWeight="medium"
                  color={textColor}
                  textAlign="left"
                >
                  ${(total_payroll || 0).toLocaleString()}
                  {team?.salary_cap !== undefined && (
                    <Text
                      as="span"
                      color={
                        total_payroll &&
                        total_payroll > Number(team.salary_cap) * 1000000
                          ? "red.400"
                          : "green.400"
                      }
                      ml={2}
                    >
                      (
                      {total_payroll &&
                      total_payroll > Number(team.salary_cap) * 1000000
                        ? ""
                        : ""}
                      $
                      {(
                       Math.abs( (total_payroll || 0) -
                        Number(team.salary_cap) * 1000000)
                      ).toLocaleString()}
                      )
                    </Text>
                  )}
                </Text>
              </VStack>
            </CardBody>
          </Card>
        </Box>
      </Flex>

      {/* View Request Details Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent bg={bgColor}>
          <ModalHeader color={textColor}>Join Request Details</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            {selectedRequest && (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text
                    fontSize="sm"
                    fontWeight="bold"
                    color={secondaryTextColor}
                    mb={1}
                  >
                    Applicant
                  </Text>
                  <Text fontSize="lg" color={textColor}>
                    {selectedRequest.user.display_name}
                  </Text>
                  <Text fontSize="sm" color={secondaryTextColor}>
                    {selectedRequest.user.email}
                  </Text>
                </Box>

                <Divider />

                <Box>
                  <Text
                    fontSize="sm"
                    fontWeight="bold"
                    color={secondaryTextColor}
                    mb={2}
                  >
                    Requested Roles
                  </Text>
                  <Text fontSize="xs" color={secondaryTextColor} mb={3}>
                    Select which roles to approve (you can approve some or all):
                  </Text>
                  <CheckboxGroup
                    value={selectedRoles}
                    onChange={(values) => setSelectedRoles(values as string[])}
                  >
                    <VStack align="start" spacing={2}>
                      {selectedRequest.requested_roles.map((role, idx) => (
                        <Checkbox key={idx} value={role} colorScheme="green">
                          <Text fontSize="sm" color={textColor}>
                            {role}
                          </Text>
                        </Checkbox>
                      ))}
                    </VStack>
                  </CheckboxGroup>
                </Box>

                <Divider />

                <Box>
                  <Text
                    fontSize="sm"
                    fontWeight="bold"
                    color={secondaryTextColor}
                    mb={2}
                  >
                    Message
                  </Text>
                  <Box
                    p={3}
                    borderRadius="md"
                    bg={messageBgColor}
                    borderWidth="1px"
                    borderColor={borderColor}
                  >
                    <Text color={textColor} whiteSpace="pre-wrap">
                      {selectedRequest.message}
                    </Text>
                  </Box>
                </Box>

                <Divider />

                <Box>
                  <Text
                    fontSize="sm"
                    fontWeight="bold"
                    color={secondaryTextColor}
                    mb={2}
                  >
                    Response Message (Optional)
                  </Text>
                  <FormControl>
                    <Textarea
                      value={responseMessage}
                      onChange={(e) => setResponseMessage(e.target.value)}
                      placeholder="Add a personal message to include with your response..."
                      size="sm"
                      rows={3}
                      resize="vertical"
                      bg={textareaBgColor}
                      borderColor={borderColor}
                      _focus={{
                        borderColor: "green.500",
                        boxShadow: "0 0 0 1px green.500",
                      }}
                    />
                  </FormControl>
                </Box>

                <Divider />

                <Box>
                  <Text
                    fontSize="sm"
                    fontWeight="bold"
                    color={secondaryTextColor}
                    mb={1}
                  >
                    Status
                  </Text>
                  {getStatusBadge(selectedRequest.status)}
                </Box>

                <Box>
                  <Text fontSize="sm" color={secondaryTextColor}>
                    Submitted on{" "}
                    {new Date(selectedRequest.created_at).toLocaleDateString()}{" "}
                    at{" "}
                    {new Date(selectedRequest.created_at).toLocaleTimeString()}
                  </Text>
                </Box>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            {selectedRequest?.status === "pending" && (
              <>
                <VStack align="stretch" spacing={3} w="full">
                  {selectedRoles.length > 0 && (
                    <Box>
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        color={secondaryTextColor}
                        mb={1}
                      >
                        Selected Roles ({selectedRoles.length}):
                      </Text>
                      <HStack spacing={1} flexWrap="wrap">
                        {selectedRoles.map((role, idx) => (
                          <Badge key={idx} colorScheme="green" fontSize="xs">
                            {role}
                          </Badge>
                        ))}
                      </HStack>
                    </Box>
                  )}
                  <HStack spacing={2}>
                    <Button
                      colorScheme="green"
                      onClick={() =>
                        selectedRequest &&
                        handleApprove(selectedRequest.id, selectedRoles)
                      }
                      isDisabled={selectedRoles.length === 0}
                    >
                      Approve Selected Roles ({selectedRoles.length})
                    </Button>
                    <Button
                      colorScheme="red"
                      variant="outline"
                      onClick={() =>
                        selectedRequest && handleDecline(selectedRequest.id)
                      }
                    >
                      Decline Request
                    </Button>
                  </HStack>
                </VStack>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Invite User Modal */}
      {isInviteOpen && (
        <InviteUserModal
          isOpen={isInviteOpen}
          onClose={onInviteClose}
          teamId={teamId}
          leagueId={leagueId}
          teamName={teamName}
          onSuccess={() => {
            setRefreshTrigger((prev) => prev + 1);
          }}
        />
      )}
    </Box>
  );
};

export default FrontOffice;

